import React, { useState } from 'react';
import { ClipboardCopy, Check as CheckIcon, ChevronDown, ChevronRight, Cpu, Wrench, Loader2 } from 'lucide-react';

import { PipelineTraceStep, ScopeGenerationResponse } from '../../types';

interface PipelineTracePanelProps {
  scope: ScopeGenerationResponse;
  /**
   * When true, the trace renders in step mode — only the first N steps are
   * visible and a "Show next" button reveals one more step at a time. Lets
   * an admin walk through the pipeline chronologically. PRP-009 §3.3.
   */
  stepMode?: boolean;
}

/**
 * Top-of-scope pipeline trace summary. Renders a chronological list of
 * each pipeline step with duration + summary. Includes a Copy-as-Markdown
 * button that flattens the entire trace (per-step + per-item LLM
 * prompt/response from every line item) into a markdown document.
 *
 * Source: scope.metadata.pipeline_trace populated by the backend when
 * debug_trace=true AND the caller is an admin. PRP-009.
 */
export const PipelineTracePanel: React.FC<PipelineTracePanelProps> = ({
  scope,
  stepMode = false,
}) => {
  const trace: PipelineTraceStep[] | undefined = scope.metadata?.pipeline_trace as
    | PipelineTraceStep[]
    | undefined;

  const [copied, setCopied] = useState<'idle' | 'ok' | 'error'>('idle');
  const [revealedCount, setRevealedCount] = useState(
    stepMode ? 1 : (trace?.length ?? 0),
  );

  // Reason: snap revealedCount when stepMode toggles or trace length changes
  React.useEffect(() => {
    if (!trace) return;
    setRevealedCount(stepMode ? 1 : trace.length);
  }, [stepMode, trace?.length]);

  if (!trace || trace.length === 0) {
    return null;
  }

  const totalDuration = trace.reduce((sum, step) => sum + (step.duration_s || 0), 0);
  const visible = trace.slice(0, revealedCount);

  const handleCopy = async () => {
    try {
      const md = buildTraceMarkdown(scope, trace);
      await navigator.clipboard.writeText(md);
      setCopied('ok');
    } catch {
      setCopied('error');
    }
    setTimeout(() => setCopied('idle'), 2000);
  };

  return (
    <div className="rounded-lg border-2 border-purple-200 bg-purple-50/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-purple-900">
            Pipeline Trace
          </h3>
          <p className="text-xs text-purple-700">
            {trace.length} steps · {totalDuration.toFixed(2)}s total
            {stepMode && revealedCount < trace.length && (
              <> · step {revealedCount} of {trace.length}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {stepMode && revealedCount < trace.length && (
            <button
              onClick={() => setRevealedCount((n) => Math.min(trace.length, n + 1))}
              className="inline-flex items-center gap-1 rounded border border-purple-400 bg-white px-3 py-1 text-xs font-medium text-purple-800 hover:bg-purple-100"
            >
              Next step →
            </button>
          )}
          {stepMode && revealedCount >= trace.length && (
            <button
              onClick={() => setRevealedCount(1)}
              className="inline-flex items-center gap-1 rounded border border-purple-400 bg-white px-3 py-1 text-xs font-medium text-purple-800 hover:bg-purple-100"
            >
              Reset
            </button>
          )}
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded border border-purple-400 bg-white px-3 py-1 text-xs font-medium text-purple-800 hover:bg-purple-100"
            title="Copy the entire pipeline trace as markdown for sharing"
          >
            {copied === 'ok' ? (
              <CheckIcon className="h-3 w-3 text-green-600" />
            ) : (
              <ClipboardCopy className="h-3 w-3" />
            )}
            {copied === 'ok' ? 'Copied' : copied === 'error' ? 'Copy failed' : 'Copy as markdown'}
          </button>
        </div>
      </div>

      <ol className="space-y-1">
        {visible.map((step, idx) => (
          <TraceStepRow key={step.step} step={step} index={idx} />
        ))}
        {stepMode && revealedCount < trace.length && (
          <li className="flex items-center gap-2 px-2 py-1 text-xs italic text-purple-500">
            <Loader2 className="h-3 w-3" />
            {trace.length - revealedCount} more step{trace.length - revealedCount === 1 ? '' : 's'} hidden — click Next to reveal
          </li>
        )}
      </ol>
    </div>
  );
};

const TraceStepRow: React.FC<{ step: PipelineTraceStep; index: number }> = ({
  step,
  index,
}) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = step.kind === 'llm' ? Cpu : Wrench;
  const hasDetail = Boolean(step.fired_rules?.length || step.summary);

  return (
    <li className="rounded border border-purple-200 bg-white">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-purple-50"
        aria-expanded={expanded}
      >
        {hasDetail ? (
          expanded ? (
            <ChevronDown className="h-3 w-3 text-purple-700" />
          ) : (
            <ChevronRight className="h-3 w-3 text-purple-700" />
          )
        ) : (
          <span className="inline-block w-3" />
        )}
        <span className="w-5 text-right font-mono text-[10px] text-purple-500">
          {index + 1}
        </span>
        <Icon className={`h-3 w-3 ${step.kind === 'llm' ? 'text-amber-600' : 'text-slate-500'}`} />
        <span className="font-mono font-medium text-purple-900">{step.step}</span>
        <span className="ml-2 text-[10px] font-medium text-slate-500">
          {step.duration_s.toFixed(3)}s
        </span>
        {step.summary && (
          <span className="ml-2 truncate text-[11px] text-slate-700">{step.summary}</span>
        )}
      </button>
      {expanded && (
        <div className="border-t border-purple-100 px-3 py-2 text-[11px] text-slate-700">
          {step.fired_rules && step.fired_rules.length > 0 && (
            <div>
              <div className="font-semibold text-slate-800">Rules fired ({step.fired_rules.length}):</div>
              <ul className="ml-3 list-disc">
                {step.fired_rules.map((r, i) => (
                  <li key={`${r.id ?? i}`}>
                    <span className="font-mono">{r.id ?? '—'}</span>
                    {r.name ? ` · ${r.name}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {step.summary && step.fired_rules?.length === undefined && (
            <div>{step.summary}</div>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Flatten the entire pipeline trace + every line item's debug payload
 * (prompt, response, candidates, resolution) into a markdown document.
 * Paste-into-chat format for diagnosing scope generation issues.
 */
function buildTraceMarkdown(
  scope: ScopeGenerationResponse,
  trace: PipelineTraceStep[],
): string {
  const lines: string[] = [];
  lines.push('# Pipeline Trace');
  lines.push('');
  const total = trace.reduce((s, t) => s + (t.duration_s || 0), 0);
  lines.push(`**${trace.length} steps · ${total.toFixed(2)}s total**`);
  lines.push('');

  lines.push('## Steps');
  lines.push('');
  trace.forEach((step, i) => {
    lines.push(`### ${i + 1}. \`${step.step}\` (${step.kind}, ${step.duration_s.toFixed(3)}s)`);
    if (step.summary) lines.push(step.summary);
    if (step.fired_rules?.length) {
      lines.push('');
      lines.push('Rules fired:');
      step.fired_rules.forEach((r) => {
        lines.push(`- \`${r.id ?? '—'}\`${r.name ? ` · ${r.name}` : ''}`);
      });
    }
    lines.push('');
  });

  // Per-line-item debug payloads
  lines.push('## Line items (with retrieval trace)');
  lines.push('');
  scope.rooms.forEach((room) => {
    lines.push(`### Room: ${room.room_name} (${room.room_type})`);
    room.items.forEach((item) => {
      const code = item.xactimate_code;
      const dbg = item.debug;
      lines.push(`#### ${code} — ${item.description}`);
      lines.push(`- activity: ${item.activity}`);
      lines.push(`- confidence: ${item.confidence}`);
      lines.push(`- source: ${item.source}`);
      if (dbg) {
        if (dbg.candidate_count !== undefined) {
          lines.push(`- candidates considered: ${dbg.candidate_count}`);
        }
        if (dbg.selection_model) {
          lines.push(`- selection model: ${dbg.selection_model}`);
        }
        if (dbg.selection_prompt) {
          lines.push('');
          lines.push('<details><summary>selection prompt</summary>');
          lines.push('');
          lines.push('```');
          lines.push(dbg.selection_prompt);
          lines.push('```');
          lines.push('');
          lines.push('</details>');
        }
        if (dbg.selection_response) {
          lines.push('');
          lines.push('<details><summary>LLM response</summary>');
          lines.push('');
          lines.push('```json');
          lines.push(JSON.stringify(dbg.selection_response, null, 2));
          lines.push('```');
          lines.push('');
          lines.push('</details>');
        }
        if (dbg.resolution) {
          lines.push('');
          lines.push('<details><summary>resolution</summary>');
          lines.push('');
          lines.push('```json');
          lines.push(JSON.stringify(dbg.resolution, null, 2));
          lines.push('```');
          lines.push('');
          lines.push('</details>');
        }
      }
      lines.push('');
    });
  });

  return lines.join('\n');
}
